var _a;
export function compactPendoMetadata(metadata) {
    const compacted = {};
    for (const [key, value] of Object.entries(metadata)) {
        if (value !== undefined) {
            compacted[key] = value;
        }
    }
    return compacted;
}
export class PendoService {
    #apiKey;
    #clientInitializer;
    #clientProvider;
    #onError;
    #client;
    #clientPromise;
    #contextMetadata = {};
    #identity;
    #pendingTracks = [];
    #sentIdentity;
    constructor(options = {}) {
        this.#apiKey = options.apiKey;
        this.#client = options.client;
        this.#clientInitializer = options.clientInitializer;
        this.#clientProvider = options.clientProvider ?? _a.#getNoopPendoClient;
        this.#onError = options.onError;
    }
    identify(identity, contextMetadata = {}) {
        this.#identity = identity;
        this.#contextMetadata = contextMetadata;
        this.#sendIdentity(identity);
    }
    clearIdentity() {
        if (!this.#sentIdentity) {
            this.#identity = undefined;
            this.#contextMetadata = {};
            return;
        }
        this.#runClientCall((client) => {
            client.clearSession?.();
        });
        this.#sentIdentity = undefined;
        this.#identity = undefined;
        this.#contextMetadata = {};
    }
    notify = (name, metadata = {}) => this.track(name, metadata);
    trackWithIdentity(identity, name, metadata = {}, contextMetadata = {}) {
        this.identify(identity, contextMetadata);
        const tracked = this.track(name, metadata);
        if (!tracked) {
            this.#pendingTracks.push({
                identity,
                name,
                metadata: {
                    ...contextMetadata,
                    ...metadata
                }
            });
        }
        return tracked;
    }
    track(name, metadata = {}) {
        if (!this.#identity) {
            return false;
        }
        const client = this.#client ?? this.#clientProvider();
        if (!client) {
            return false;
        }
        this.#client = client;
        if (!this.#sentIdentity ||
            !this.#areIdentitiesEqual(this.#sentIdentity, this.#identity)) {
            const sentIdentity = this.#sendIdentityToClient(client, this.#identity, this.#createOptions(this.#identity));
            if (!sentIdentity) {
                return false;
            }
        }
        return this.#runClientCall((client) => {
            client.track?.(name, {
                ...this.#contextMetadata,
                ...metadata
            });
        });
    }
    #sendIdentity(identity) {
        if (this.#sentIdentity && this.#areIdentitiesEqual(this.#sentIdentity, identity)) {
            return;
        }
        const options = this.#createOptions(identity);
        const client = this.#client ?? this.#clientProvider();
        if (client) {
            this.#client = client;
            this.#sendIdentityToClient(client, identity, options);
            return;
        }
        if (!this.#clientInitializer) {
            return;
        }
        this.#clientPromise ??= Promise.resolve(this.#clientInitializer(options));
        void this.#clientPromise
            .then((initializedClient) => {
            this.#client = initializedClient;
            if (!this.#identity) {
                if (this.#hasPendingTracksForIdentity(identity)) {
                    this.#sentIdentity = identity;
                    this.#flushPendingTracks(initializedClient, identity);
                    return;
                }
                this.#runClientCall((client) => {
                    client.clearSession?.();
                });
                return;
            }
            if (this.#areIdentitiesEqual(this.#identity, identity)) {
                this.#sentIdentity = identity;
                this.#flushPendingTracks(initializedClient, identity);
                return;
            }
            this.#sentIdentity = identity;
            this.#flushPendingTracks(initializedClient, identity);
            this.#sendIdentityToClient(initializedClient, this.#identity, this.#createOptions(this.#identity));
        })
            .catch((error) => {
            this.#clientPromise = undefined;
            this.#onError?.(error);
        });
    }
    #createOptions(identity) {
        if (!this.#apiKey) {
            return identity;
        }
        return {
            ...identity,
            apiKey: this.#apiKey
        };
    }
    #runClientCall(call) {
        const client = this.#client ?? this.#clientProvider();
        if (!client) {
            return false;
        }
        try {
            call(client);
            return true;
        }
        catch (error) {
            this.#onError?.(error);
            return false;
        }
    }
    #areIdentitiesEqual(left, right) {
        return (_a.#areMetadataEqual(left.visitor, right.visitor) &&
            _a.#areMetadataEqual(left.account, right.account));
    }
    #sendIdentityToClient(client, identity, options) {
        const sent = this.#runClientCall((readyClient) => {
            if (!this.#sentIdentity && readyClient.initialize) {
                readyClient.initialize(options);
                return;
            }
            if (this.#sentIdentity?.visitor.id === identity.visitor.id && readyClient.updateOptions) {
                readyClient.updateOptions(options);
                return;
            }
            if (readyClient.identify) {
                readyClient.identify(options);
                return;
            }
            if (readyClient.updateOptions) {
                readyClient.updateOptions(options);
            }
        });
        if (sent) {
            this.#client = client;
            this.#sentIdentity = identity;
            this.#flushPendingTracks(client, identity);
        }
        return sent;
    }
    #hasPendingTracksForIdentity(identity) {
        return this.#pendingTracks.some((pendingTrack) => this.#areIdentitiesEqual(pendingTrack.identity, identity));
    }
    #flushPendingTracks(client, identity) {
        const pendingTracks = this.#pendingTracks;
        this.#pendingTracks = [];
        for (const pendingTrack of pendingTracks) {
            if (!this.#areIdentitiesEqual(pendingTrack.identity, identity)) {
                this.#pendingTracks.push(pendingTrack);
                continue;
            }
            this.#runClientCall((readyClient) => {
                readyClient.track?.(pendingTrack.name, pendingTrack.metadata);
            });
        }
    }
    static #areMetadataEqual(left, right) {
        return JSON.stringify(left) === JSON.stringify(right);
    }
    static #getNoopPendoClient() {
        return undefined;
    }
}
_a = PendoService;
